/**
 * URL-Hash-Navigation: aktualisiert den Hash beim Sektionswechsel,
 * reagiert auf den Zurück-Button und fängt Anker-Klicks ab.
 */

export class HashRouter {
	/**
	 * @param {Object} options
	 *   anchors     Array<string|null> — Anchor je Sektion (null = keiner)
	 *   onNavigate  (index) => void — Ziel ansteuern (animiert)
	 */
	constructor( { anchors, onNavigate } ) {
		this.anchors = anchors;
		this.onNavigate = onNavigate;
		this.current = this.readHash();

		this.onHashChange = this.onHashChange.bind( this );
		this.onClick = this.onClick.bind( this );

		window.addEventListener( 'hashchange', this.onHashChange );
		document.addEventListener( 'click', this.onClick );
	}

	readHash() {
		try {
			return decodeURIComponent( window.location.hash.slice( 1 ) );
		} catch ( e ) {
			return window.location.hash.slice( 1 );
		}
	}

	/**
	 * Index für den beim Laden vorhandenen Hash, sonst null.
	 */
	initialIndex() {
		const index = this.anchors.indexOf( this.readHash() );
		return index >= 0 ? index : null;
	}

	/**
	 * Nach einem Sektionswechsel den Hash in die History schreiben.
	 */
	update( index, replace = false ) {
		const anchor = this.anchors[ index ];

		if ( ! anchor || this.current === anchor ) {
			return;
		}

		this.current = anchor;

		if ( replace ) {
			window.history.replaceState( null, '', '#' + anchor );
		} else {
			window.history.pushState( null, '', '#' + anchor );
		}
	}

	onHashChange() {
		const anchor = this.readHash();

		if ( anchor === this.current ) {
			return;
		}

		this.current = anchor;

		if ( anchor === '' ) {
			this.onNavigate( 0 );
			return;
		}

		const index = this.anchors.indexOf( anchor );

		if ( index >= 0 ) {
			this.onNavigate( index );
		}
	}

	/**
	 * Klicks auf Seiten-interne Anker-Links abfangen, damit der Browser
	 * nicht hart springt, sondern die Engine animiert navigiert.
	 */
	onClick( event ) {
		if ( event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey ) {
			return;
		}

		const link =
			event.target instanceof Element ? event.target.closest( 'a[href*="#"]' ) : null;

		if ( ! link ) {
			return;
		}

		const url = new URL( link.href, window.location.href );

		if (
			url.pathname !== window.location.pathname ||
			url.origin !== window.location.origin
		) {
			return;
		}

		const anchor = decodeURIComponent( url.hash.slice( 1 ) );
		const index = this.anchors.indexOf( anchor );

		if ( index < 0 ) {
			return;
		}

		event.preventDefault();
		this.current = anchor;
		window.history.pushState( null, '', '#' + anchor );
		this.onNavigate( index );
	}

	destroy() {
		window.removeEventListener( 'hashchange', this.onHashChange );
		document.removeEventListener( 'click', this.onClick );
	}
}
